import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Form, Input, Checkbox, Message } from '@b-design/fusion';
import styles from './index.module.less';
import $i18n from '../../../../i18n';
import Requests from '@/services/request';
import { Field, Icon } from '@alifd/next';
import Button, { FormSubmit, promisifyValidate } from '@/components/Button';

interface ForgotPasswordFormProps {
  changeIsLogin: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ changeIsLogin }) => {
  const field = Field.useField();
  const { getValue, validate } = field;

  const [loading, setLoading] = useState<boolean>(false);
  // email是否验证通过
  const [readySendCode, setReadySendCode] = useState<boolean>(false);

  // 倒计时
  const [time, setTime] = useState<number>(0);

  // 提交按钮
  const submitForm = async (value, error) => {
    if (error) {
      return;
    }
    const { account, password, verifyCode } = value;
    const params = {
      account,
      password,
      verifyCode,
    };
    const res = await Requests.post('/rest/intercnn/user/modify', { ...params });
    if (res) {
      Message.success({
        title: $i18n.get({
          id: 'aopf.pages.channel.channelList.message.success',
          dm: '修改成功',
        }),
      });
      changeIsLogin();
    }
  };

  const onValidate = async () => {
    const [errs, vals] = await promisifyValidate(field);
    if (errs) {
      return;
    }
    await submitForm(vals, errs);
  };

  // 邮箱验证
  const accountValidator = (rule, value) => {
    return new Promise((resolve, reject) => {
      const regex = /^[A-Za-z\d]+([-_.][A-Za-z\d]+)*@([A-Za-z\d]+[-.])+[A-Za-z\d]{2,}$/;
      if (!regex.test(value)) {
        // setTime(0);
        setReadySendCode(false);
        return reject(
          $i18n.get({
            id: 'aopf.pages.login.registerForm.email.msg',
            dm: '请输入正确的邮箱格式',
          }),
        );
      } else {
        setReadySendCode(true);
        resolve(null);
      }
    });
  };

  // 密码验证
  const passwordValidator = (rule, value) => {
    return new Promise<void>((resolve, reject) => {
      // const regex = / ^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#&()-\[{}\]:\;',?/*~$^+=<>]).{8,20}$/;
      if (value) {
        validate(['confirmPassword']);
      }
      const regNum = /(\d)+/g;
      const regUppercase = /[A-Z]+/g;
      const regLowercase = /[a-z]+/g;
      const regSpecial = /([!@#&()\-\[{}\]:\;',?/*~$^+=<>])+/g;
      const regComplete = /^[a-zA-Z0-9!@#&()\-\[{}\]:\;',?/*~$^+=<>]{8,20}$/;

      if (!regNum.test(value)) {
        return reject(
          $i18n.get({
            id: 'aopf.pages.login.registerForm.password.number',
            dm: '请至少输入一个数字',
          }),
        );
      } else if (!regUppercase.test(value)) {
        return reject(
          $i18n.get({
            id: 'aopf.pages.login.registerForm.password.uppercase',
            dm: '请至少输入一个大写字母',
          }),
        );
      } else if (!regLowercase.test(value)) {
        return reject(
          $i18n.get({
            id: 'aopf.pages.login.registerForm.password.lowercase',
            dm: '请至少输入一个小写字母',
          }),
        );
      } else if (!regSpecial.test(value)) {
        return reject(
          $i18n.get({
            id: 'aopf.pages.login.registerForm.password.special',
            dm: '请至少输入一个特殊字符',
          }),
        );
      } else if (value?.length < 8 || value?.length > 20) {
        return reject(
          $i18n.get({
            id: 'aopf.pages.login.registerForm.password.length',
            dm: '请输入8-20个字符',
          }),
        );
      } else if (!regComplete.test(value)) {
        return reject(
          $i18n.get({
            id: 'aopf.pages.login.registerForm.password.notAllowspecial',
            dm: '输入了非法字符',
          }),
        );
      } else {
        resolve();
      }
    });
  };

  // 确认密码验证
  const confirmPasswordValidator = (rule, value) => {
    return new Promise((resolve, reject) => {
      if (value && value !== getValue('password')) {
        return reject(
          $i18n.get({
            id: 'aopf.pages.login.registerForm.inconsistent.msg',
            dm: '前后密码不一致。',
          }),
        );
      } else {
        return resolve(null);
      }
    });
  };

  const counting = useCallback((time = 180) => {
    if (time < 0) return;
    setTime(time);
    setTimeout(() => {
      counting(time - 1);
    }, 1000);
  }, []);

  // 校验email并发送验证码
  const handleValidateAccount = useCallback(async () => {
    const [err, values] = await promisifyValidate(field, 'account');
    if (err) {
      return;
    }
    // todo
    await Requests.post('/rest/intercnn/user/global/sendModifyCode', { mail: values?.account });
    counting();
  }, [counting]);

  return (
    <div className={styles.forgotPasswordForm}>
      <div className={styles.forgotPasswordText}>
        {$i18n.get({
          id: 'aopf.pages.login.forgotPasswordTitle',
          dm: '忘记密码',
        })}
      </div>
      <div className={styles.forgotPasswordDesc}>
        {$i18n.get({
          id: 'aopf.pages.login.forgotPasswordDesc',
          dm: '输入您登录的电子邮件地址，我们将向您发送一个验证码以验证您的身份。',
        })}
      </div>
      <Form field={field} style={{ maxWidth: '400px', margin: '0 auto', marginTop: '16px' }}>
        <Form.Item
          style={{ marginBottom: '0px' }}
          name="account"
          label={$i18n.get({
            id: 'aopf.pages.login.registerForm.label.account',
            dm: '账号',
          })}
          required
          requiredMessage={$i18n.get({
            id: 'aopf.pages.login.registerForm.email.required',
            dm: '请输入邮箱！',
          })}
          validator={accountValidator.bind(null)}
        >
          <Input
            placeholder={$i18n.get({
              id: 'aopf.pages.login.registerForm.account.placeholder',
              dm: 'example@email.com',
            })}
            showLimitHint
            maxLength={64}
            style={{ width: '67%', marginRight: '12px' }}
            onPressEnter={onValidate}
          />
          {time === 0 && (
            <Button
              type="secondary"
              onClick={handleValidateAccount}
              style={{ width: '120px' }}
              disabled={!readySendCode}
            >
              {$i18n.get({
                id: 'aopf.pages.login.signUp.sendCode',
                dm: '发送验证码',
              })}
            </Button>
          )}
          {time > 0 && (
            <Button type="secondary" disabled={true} style={{ width: '120px' }}>
              {$i18n.get({
                id: 'aopf.pages.login.signUp.reSendCode',
                dm: '重新发送',
              }) + `(${time})`}
            </Button>
          )}
        </Form.Item>

        <Form.Item
          style={{ marginBottom: '0px' }}
          name="verifyCode"
          label={$i18n.get({
            id: 'aopf.pages.login.registerForm.label.verificationCode',
            dm: '验证码',
          })}
          required
          requiredMessage={$i18n.get({
            id: 'aopf.pages.login.registerForm.require.verificationCode',
            dm: '请输入验证码',
          })}
          minLength={6}
          maxLength={6}
          minmaxLengthMessage={$i18n.get({
            id: 'aopf.pages.login.registerForm.require.verificationCode.length',
            dm: '请输入6位验证码',
          })}
        >
          <Input
            placeholder={$i18n.get({
              id: 'aopf.pages.login.registerForm.label.verificationCode.placeholder',
              dm: '请输入验证码',
            })}
            onPressEnter={onValidate}
          />
        </Form.Item>

        <Form.Item
          style={{ marginBottom: '0px' }}
          name="password"
          label={$i18n.get({
            id: 'aopf.pages.login.registerForm.label.newPassword',
            dm: '新密码',
          })}
          required
          requiredMessage={$i18n.get({
            id: 'aopf.pages.login.PleaseEnterThePassword',
            dm: '请输入您的密码',
          })}
          validator={passwordValidator.bind(null)}
        >
          <Input.Password
            placeholder={$i18n.get({
              id: 'aopf.pages.login.registerForm.password.placeholder',
              dm: '8-20个字符',
            })}
            onPressEnter={onValidate}
          />
        </Form.Item>

        <Form.Item
          style={{ marginBottom: '0px' }}
          name="confirmPassword"
          label={$i18n.get({
            id: 'aopf.pages.login.registerForm.label.confirmPassword',
            dm: '确认密码',
          })}
          required
          requiredMessage={$i18n.get({
            id: 'aopf.pages.login.PleaseEnterTheConfirPassword',
            dm: '请输入确认密码！',
          })}
          validator={confirmPasswordValidator.bind(null)}
        >
          <Input.Password
            placeholder={$i18n.get({
              id: 'aopf.pages.login.registerForm.confirmPassword.placeholder',
              dm: '再次输入密码',
            })}
            onPressEnter={onValidate}
          />
        </Form.Item>

        <Form.Item name="btn">
          <FormSubmit
            type="primary"
            onClick={onValidate}
            validate
            style={{
              height: 40,
              width: 400,
              borderRadius: 4,
              fontWeight: 500,
              fontSize: 16,
              color: '#fff',
              letterSpacing: '0.15px',
              lineHeight: '24px',
              position: 'absolute',
              bottom: 0,
            }}
          >
            {$i18n.get({
              id: 'aopf.pages.login.forgotPassword.changePassword',
              dm: '修改密码',
            })}
          </FormSubmit>
        </Form.Item>
      </Form>
    </div>
  );
};

export default ForgotPasswordForm;
